import { Injectable, Injector } from '@angular/core';
import { ApplicationServiceProxy, ApplicationUploadFilesInput, CreateOrUpdatePdfPersonInput, CreateOrUpdatePdfPersonOutput, CreateOrUpdatePdfSponsorshipInput, CreateOrUpdatePdfSponsorshipOutput, CreatePdfInput, CreatePdfOutput, GeneralDocumentGroupDto, GeneralFileDto, GeneralFileInfoDto, GetPdfOutput, PdfApplicationDto, PdfDocumentServiceProxy, PdfDocumentsOutput, PdfPersonDto, PdfPersonServiceProxy, PdfSponsorshipDto, PdfSponsorshipServiceProxy, PdfStatus, PdfSummaryDto } from '@shared/service-proxies/service-proxies';
import { PdfApplicationServiceProxy } from '../../../shared/service-proxies/service-proxies';
import { ApplicationService } from '../application/application.service';
import { CodeService } from '../common/code/code.service';
import { Observable, of, ReplaySubject } from 'rxjs';
import { tap, switchMap, map, delay } from 'rxjs/operators';

@Injectable()
export class PdfService extends ApplicationService {
    app: PdfApplicationDto | null = null;
    private pdfsForPersonSubject = new ReplaySubject<PdfSummaryDto[]>(1);
    readonly pdfsForPerson$ = this.pdfsForPersonSubject.asObservable();
    private pdfsForSponsorshipSubject = new ReplaySubject<PdfSummaryDto[]>(1);
    readonly pdfsForSponsorship$ = this.pdfsForSponsorshipSubject.asObservable();

    constructor(
        injector: Injector,
        codeService: CodeService,
        private appService: ApplicationServiceProxy,
        private pdfAppService: PdfApplicationServiceProxy,
        private pdfPersonService: PdfPersonServiceProxy,
        private pdfSponsorshipService: PdfSponsorshipServiceProxy,
        private pdfDocumentService: PdfDocumentServiceProxy
    ) {
        super(injector, codeService, appService);
    }

    createPdf(input: CreatePdfInput): Observable<CreatePdfOutput> {
        return this.pdfAppService.createPdfApplication(input).pipe(tap((output: CreatePdfOutput) => {
            this.app = null;
        }));
    }

    getPdf(caseId: string): Observable<GetPdfOutput> {
        this.app = null;
        return this.getApplication(caseId).pipe(
            switchMap(() => this.pdfAppService.getPdfApplication(caseId).pipe(
                tap((output: GetPdfOutput) => {
                    this.app = output.pdfApplication;
                })
            )
        ));
    }

    getMyPdfs(): Observable<PdfApplicationDto[]> {
        return this.pdfAppService.getMyPdfs();
    }

    loadPdfsForPerson(personGln?: string, personId?: number): Observable<PdfSummaryDto[]> {
        if (personGln || personId) {
            return this.pdfAppService.getPdfsForPerson(personGln, personId).pipe(
                tap(pdfs => this.pdfsForPersonSubject.next(pdfs))
            );
        } else {
            this.pdfsForPersonSubject.next([]);
            return of([]);
        }
    }

    loadPdfsForSponsorship(sponsorshipUid?: string, sponsorshipId?: number): Observable<PdfSummaryDto[]> {
        sponsorshipUid = sponsorshipUid || '';
        if (sponsorshipUid || sponsorshipId) {
            return this.pdfAppService.getPdfsForSponsorship(sponsorshipUid, sponsorshipId).pipe(
                tap(pdfs => this.pdfsForSponsorshipSubject.next(pdfs))
            );
        } else {
            this.pdfsForSponsorshipSubject.next([]);
            return of([]);
        }
    }

    getUrlForStep(step: PdfStatus): string {
        switch (step) {
            case PdfStatus.Person:
                return 'person';
            case PdfStatus.Sponsorship:
                return 'sponsorship';
            case PdfStatus.Release:
                return 'release';
            case PdfStatus.FinishReview:
                return 'finish-review';
            default:
                console.error('Unknown step: ' + step);
                return '';
        }
    }

    getFile(step: PdfStatus, id: number): Observable<GeneralFileDto> {
        return this.pdfDocumentService.getFile(this._caseId, step, id);
    }

    uploadPersonFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        input.step = PdfStatus.Person;
        return this.pdfDocumentService.uploadFiles(input);
    }

    uploadSponsorshipFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        input.step = PdfStatus.Sponsorship;
        return this.pdfDocumentService.uploadFiles(input);
    }

    deleteFile(step: PdfStatus, id: number): Observable<void> {
        return this.pdfDocumentService.deleteFile(this._caseId, step, id);
    }

    isInPdfStatus(expectedStatus: PdfStatus): boolean {
        return this.app?.currentStep === expectedStatus;
    }

    getPersonFiles(forceRefresh?: boolean, caseId = this._caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.pdfDocumentService.getDocuments(caseId, PdfStatus.Person).pipe(
                map((output: PdfDocumentsOutput) => output.pdfDocuments),
                tap((pdfDocuments: GeneralDocumentGroupDto[]) => {
                    if (this.app) {
                        this.app.pdfDocuments = pdfDocuments;
                    }
                })
            );
        }
        return of(this.app?.pdfDocuments || []);
    }

    getSponsorshipFiles(forceRefresh?: boolean, caseId = this._caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.pdfDocumentService.getDocuments(caseId, PdfStatus.Sponsorship).pipe(
                map((output: PdfDocumentsOutput) => output.pdfDocuments),
                tap((pdfDocuments: GeneralDocumentGroupDto[]) => {
                    if (this.app) {
                        this.app.pdfDocuments = pdfDocuments;
                    }
                })
            );
        }
        return of(this.app?.pdfDocuments || []);
    }

    getPerson(): Observable<PdfPersonDto | undefined> {
        return of(this.app?.pdfPerson).pipe(delay(1));
    }

    getSponsorship(): Observable<PdfSponsorshipDto | undefined> {
        return of(this.app?.pdfSponsorship).pipe(delay(1));
    }

    getNextStatus(status: PdfStatus): Observable<PdfStatus> {
        let isAuthority = this.permission.isGranted('Pages.Authority.Applications');
        return this.pdfAppService.getNextStatus(this._caseId, status).pipe(map(status => {
            // Authority goes to 'finish review' and not to 'release'
            if (isAuthority && status === PdfStatus.Release) {
                return PdfStatus.FinishReview;
            }
            return status;
        }));
    }

    getStepUrl(nextStep: PdfStatus): string {
        return this.getNextStep(this.getUrlForStep(nextStep));
    }

    createOrUpdatePdfPerson(input: CreateOrUpdatePdfPersonInput): Observable<CreateOrUpdatePdfPersonOutput> {
        this.setStepToReview(PdfStatus.Person);
        return this.pdfPersonService.createOrUpdatePdfPerson(input).pipe(tap((output: CreateOrUpdatePdfPersonOutput) => {
            if (this.app) {
                this.app.pdfPerson = output.pdfPerson;
            }
        }));
    }

    getPdfApplications(filter: string, sorting: string, maxResultCount: number, skipCount: number) {
        return this.pdfAppService.getPdfApplications(
            filter,
            sorting,
            maxResultCount,
            skipCount
        );
    }

    isPersonApplicationType(pdfApplicationTypeId: string): boolean {
        const personApplicationTypes = [
            'PdfApplicationType-Lia',
            'PdfApplicationType-LiaRen',
            'PdfApplicationType-Lia90Days',
            'PdfApplicationType-Ehc',
            'PdfApplicationType-EhcRen',
            'PdfApplicationType-Ehc90Days',
            'PdfApplicationType-Alap',
            'PdfApplicationType-Rla',
            'PdfApplicationType-Tla',
            'PdfApplicationType-Slap',
            'PdfApplicationType-SlapRen',
            'PdfApplicationType-Spl',
            'PdfApplicationType-TiPr',
            'PdfApplicationType-Cogs'
        ];
        return personApplicationTypes.includes(pdfApplicationTypeId);
    }

    isSponsorshipApplicationType(pdfApplicationTypeId: string): boolean {
        const sponsorshipApplicationTypes = [
            'PdfApplicationType-Cla',
            'PdfApplicationType-ClaRen',
            'PdfApplicationType-ClaEhc',
            'PdfApplicationType-ClaEhcRen',
            'PdfApplicationType-Alas',
            'PdfApplicationType-Slas',
            'PdfApplicationType-SlasRen',
            'PdfApplicationType-Ola',
            'PdfApplicationType-OlaRen',
            'PdfApplicationType-Nla',
            'PdfApplicationType-NlaRen',
            'PdfApplicationType-Bla',
            'PdfApplicationType-BlaRen'
        ];
        return sponsorshipApplicationTypes.includes(pdfApplicationTypeId);
    }

    getEvaluationComments(): Observable<string> {
        return this.pdfAppService.getEvaluationComments(this._caseId);
    }

    getPersonId(): Observable<number> {
        return this.pdfAppService.getPersonId(this._caseId);
    }

    getSponsorshipId(): Observable<number> {
        return this.pdfAppService.getSponsorshipId(this._caseId);
    }

    checkDocuments(): Observable<PdfStatus[]> {
        return this.pdfAppService.validateThatAllMandatoryDocumentsHaveBeenUploaded(this._caseId);
    }

    releasePdf(): Observable<void> {
        return this.pdfAppService.releasePdf(this._caseId);
    }

    createOrUpdatePdfSponsorship(input: CreateOrUpdatePdfSponsorshipInput): Observable<CreateOrUpdatePdfSponsorshipOutput> {
        this.setStepToReview(PdfStatus.Sponsorship);
        return this.pdfSponsorshipService.createOrUpdateSponsorship(input).pipe(tap((output: CreateOrUpdatePdfSponsorshipOutput) => {
            if (this.app) {
                this.app.pdfSponsorship = output.sponsorship;
            }
        }));
    }
}
